import { SectionBackground } from '@/components/ui/section-background';
import { TrophyIcon } from '@/components/challenge/trophy-icon';
import { CHALLENGE } from '@/lib/challenge-data';
import {
  useChallengeCompletions,
  ChallengeCompletion,
} from '@/lib/hooks/use-challenge';

/**
 * Challenges the user has finished.
 *
 * Just the trophy and the details for now — the certificate itself
 * (components/challenge/certificate.tsx) is built but deliberately not shown
 * here yet.
 */

/** Only one challenge exists so far; look its details up by slug. */
function challengeFor(slug: string) {
  return slug === CHALLENGE.slug
    ? { theme: CHALLENGE.theme, days: CHALLENGE.days.length }
    : { theme: slug, days: 7 };
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function CompletionRow({ completion }: { completion: ChallengeCompletion }) {
  const { theme, days } = challengeFor(completion.challenge_slug);

  return (
    <div className="flex items-center gap-4">
      <TrophyIcon className="flex-shrink-0 h-10 w-10" />

      <div className="min-w-0">
        <p className="font-medium truncate">
          {days}-daagse challenge · {theme}
        </p>
        <p className="text-sm text-muted-foreground">
          Afgerond op {formatDate(completion.completed_at)}
        </p>
      </div>
    </div>
  );
}

/** Hidden entirely when the user has none, so the profile doesn't grow an empty box. */
export function CertificatesSection() {
  const { completions, loading } = useChallengeCompletions();

  if (loading || completions.length === 0) return null;

  return (
    <SectionBackground className="p-6 bg-transparent border-2 border-[#8BC34A] rounded-lg">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Afgeronde challenges</h3>
        <div className="space-y-3">
          {completions.map((completion) => (
            <CompletionRow key={completion.id} completion={completion} />
          ))}
        </div>
      </div>
    </SectionBackground>
  );
}
